import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';
import type {
  TextInputProps,
  TextProps,
  ViewProps,
  ViewStyle,
} from 'react-native';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import BookmarkFillSvg from '../../assets/bookmark_fill.svg';
import ContentScrollView from '../components/ContentScrollView';
import FixedWidthRowView from '../components/FixedWidthRowView';
import type {_Array, _Object} from '../Paramut';
import {useTheme, variance} from '../styling';

export type ListCitiesScreenProps = {
  cityName?: string;
  onCityNameChange?: (_: string) => void;
  placeholder?: string;
  favorites?: Cities | null;
  search?: Cities | null;
  onCityPress?: (id: number) => void;
  compensateHeaderHeight?: number;
};

export type Cities<Mut extends boolean = false> = _Array<City<Mut>>;

export type City<Mut extends boolean = false> = _Object<
  {
    id: number;
    name: string;
  },
  Mut
>;

export default observer(function ListCitiesScreen(
  props: ListCitiesScreenProps,
) {
  const {
    placeholder,
    cityName,
    onCityNameChange,
    favorites,
    search,
    onCityPress,
    compensateHeaderHeight,
  } = props;
  const contentContainerStyle: ViewStyle = useMemo(
    () => ({
      paddingTop: compensateHeaderHeight ?? 0,
    }),
    [compensateHeaderHeight],
  );
  const topIsCompensated = compensateHeaderHeight !== undefined;
  return (
    <ContentScrollView
      contentContainerStyle={contentContainerStyle}
      topIsCompensated={topIsCompensated}>
      <FixedWidthRowView>
        <CityTextInput
          style={layoutStyles.input}
          value={cityName}
          onChangeText={onCityNameChange}
          placeholder={placeholder}
        />
      </FixedWidthRowView>
      <View
        style={[layoutStyles.bottomGap, layoutStyles.grow, layoutStyles.gap]}>
        {favorites === undefined || search == undefined ? (
          <FixedWidthRowView
            style={layoutStyles.grow}
            innerStyle={[layoutStyles.center]}>
            <QueryMetaText>Загрузка...</QueryMetaText>
          </FixedWidthRowView>
        ) : (
          <>
            {hasItems(favorites) &&
              favorites.map(_ => (
                <FixedWidthRowView key={_.id}>
                  <FavoriteCityItemView
                    cityId={_.id}
                    name={_.name}
                    onCityPress={onCityPress}
                  />
                </FixedWidthRowView>
              ))}
            {hasItems(favorites) && hasItems(search) && (
              <FixedWidthRowView style={layoutStyles.divider}>
                <Divider />
              </FixedWidthRowView>
            )}
            {hasItems(search)
              ? search.map(_ => (
                  <FixedWidthRowView key={_.id}>
                    <CityItemView
                      cityId={_.id}
                      name={_.name}
                      onCityPress={onCityPress}
                    />
                  </FixedWidthRowView>
                ))
              : !hasItems(favorites) && (
                  <FixedWidthRowView
                    style={layoutStyles.grow}
                    innerStyle={[layoutStyles.center]}>
                    <QueryMetaText>Нет городов по такому запросу</QueryMetaText>
                  </FixedWidthRowView>
                )}
          </>
        )}
      </View>
    </ContentScrollView>
  );
});

const layoutStyles = StyleSheet.create({
  input: {
    marginVertical: 24,
  },
  gap: {
    gap: 8,
  },
  bottomGap: {
    marginBottom: 24,
  },
  grow: {
    flexGrow: 1,
    flexShrink: 0,
  },
  center: {
    justifyContent: 'center',
  },
  divider: {
    marginVertical: 16,
  },
  item: {
    flexDirection: 'row',
  },
  itemText: {
    flexGrow: 1,
    flexShrink: 1,
  },
  itemIcon: {
    flexGrow: 0,
    flexShrink: 0,
  },
});

const CityTextInput = variance(TextInput)(
  theme => ({
    root: {
      height: HEIGHT,
      borderWidth: 1,
      borderColor: theme.palette.borderPrimary,
      borderRadius: HEIGHT / 2,
      paddingVertical: PADDING_VERTICAL,
      paddingHorizontal: 24,
      ...theme.text('base'),
      color: theme.palette.textPrimary,
      fontSize: 17,
      lineHeight: HEIGHT - 2 * PADDING_VERTICAL,
      backgroundColor: theme.palette.backgroundAverage,
    },
  }),
  (theme): TextInputProps => ({
    inputMode: 'text',
    autoComplete: Platform.select({
      ios: 'address-line1',
      android: 'postal-address-locality',
      default: 'address-level2',
    }) as TextInputProps['autoComplete'],
    autoCorrect: true,
    hitSlop,
    keyboardAppearance: theme.isDark ? 'dark' : 'light',
    placeholderTextColor: theme.palette.textSecondary,
  }),
);

const HEIGHT = 32;
const PADDING_VERTICAL = 4;

const hitSlop = {left: 16, top: 8, right: 16, bottom: 8} as const;

const QueryMetaText = variance(Text)(theme => ({
  root: {
    ...theme.text('base'),
    fontSize: 19,
    lineHeight: 27,
    color: theme.palette.textPrimary,
    textAlign: 'center',
  },
}));

export type CityItemViewProps = {
  cityId: number;
  name: string;
  onCityPress?: (id: number) => void;
} & ViewProps;

const CityItemView = observer(function (props: CityItemViewProps) {
  const {cityId, name, onCityPress, ...rest} = props;

  const onPress = useCallback(() => {
    onCityPress?.(cityId);
  }, [cityId, onCityPress]);

  return (
    <CityNameText onPress={onPress} {...rest}>
      {name}
    </CityNameText>
  );
});

const FavoriteCityItemView = observer(function (props: CityItemViewProps) {
  const {cityId, name, onCityPress, style, ...rest} = props;

  const onPress = useCallback(() => {
    onCityPress?.(cityId);
  }, [cityId, onCityPress]);

  const theme = useTheme();

  return (
    <Pressable onPress={onPress} style={[layoutStyles.item, style]} {...rest}>
      <CityNameText style={layoutStyles.itemText}>{name}</CityNameText>
      <View style={layoutStyles.itemIcon}>
        <BookmarkFillSvg color={theme.palette.textPrimary} />
      </View>
    </Pressable>
  );
});

const CityNameText = variance(Text)(
  theme => ({
    root: {
      ...theme.text('header6'),
      fontSize: 19,
      lineHeight: 27,
      color: theme.palette.textPrimary,
    },
  }),
  (): TextProps => ({
    numberOfLines: 1,
    ellipsizeMode: 'tail',
    role: 'link',
  }),
);

const Divider = variance(View)(theme => ({
  root: {
    alignSelf: 'stretch',
    height: 1,
    backgroundColor: theme.palette.textSecondary,
  },
}));

function hasItems<T>(_: readonly T[] | null): _ is readonly [T, ...T[]] {
  return _ !== null && _.length > 0;
}
